import React from 'react';

import {
	Box,
	Checkbox,
	DividerLine,
	DividerLineProps,
	Spacer,
	TextBlock,
	Typography,
	useTheme,
	Walkthrough,
} from '@bcp-nextgen-dx-component-factory/design-system';
import { ComponentInfo, ComposableTextLink, DisplayCost } from '@components';
import { rem } from '@helpers';

import { AtmAndMbway } from './block';
import { usePaymentOptionsHelper } from './paymentOptions.helper';

export interface SummaryItem {
	description: string;
	cost: number;
}

export const PaymentOptionsPlaceholder: React.FC = () => {
	const { resources, acceptMasterOrVisaCards, solutionIntegrated, walkthroughSteps, handleCheckBoxOnClick } =
		usePaymentOptionsHelper();

	const { colorPalette } = useTheme();

	return (
		<ComponentInfo name="PaymentMethodsPlaceholder" type="placeholder">
			<Box styles={{ marginBottom: rem(40) }}>
				<Typography appearance="headline-200-03">{resources.titlePage}</Typography>
			</Box>
			<Box
				styles={{
					padding: rem(32, 24),
					backgroundColor: colorPalette.color_01[50],
					borderRadius: rem(8),
					flexDirection: 'column',
				}}
			>
				<Box
					styles={{
						justifyContent: 'space-between',
					}}
				>
					<Box
						styles={{
							flexDirection: 'column',
						}}
					>
						<Typography appearance="body-100-04">{resources.titleCard}</Typography>
						<Spacer vertical={rem(32)} />
						<AtmAndMbway />
					</Box>
					<Box
						styles={{
							flexDirection: 'column',
							alignItems: 'flex-end',
						}}
					>
						{solutionIntegrated ? (
							<>
								<TextBlock.Currency
									floatValue={parseFloat(resources.valueWithoutPromotion)}
									size="medium"
									colorToken="interaction-03-90"
									strikeThrough={true}
									currency={resources.eur.toUpperCase()}
									timeCourse={resources.month}
								/>
								<DisplayCost cost={resources.valueWithPromotion} appearance={'promotionalMain'} />
							</>
						) : (
							<>
								<DisplayCost cost={resources.valueWithoutPromotion} appearance={'main'} />
								<Typography appearance="body-75-02" colorToken="secondary-light">
									{resources.withIva}
								</Typography>
							</>
						)}
					</Box>
				</Box>
				<Box styles={{ flexDirection: 'column', alignItems: 'center' }}>
					<DividerLine {...({ margin: rem(24, 0) } as DividerLineProps)} />
				</Box>
				<Box
					styles={{
						padding: rem(16),
						gap: rem(10),
						backgroundColor: '#F5F6FA',
						borderRadius: rem(8),
						flexDirection: 'column',
					}}
				>
					<Box styles={{ alignItems: 'center' }}>
						<div style={{ marginRight: rem(16) }}>
							<Checkbox checked={acceptMasterOrVisaCards} size="small" onChange={handleCheckBoxOnClick} />
						</div>
						<Typography appearance="body-75-03">{resources.acceptCheck}</Typography>
					</Box>
					{acceptMasterOrVisaCards && (
						<Box
							styles={{
								padding: rem(16, 8, 8, 8),
								flexDirection: 'column',
							}}
						>
							<Typography appearance="body-75-02">{resources.disclaimerOne}</Typography>
							<Spacer vertical={rem(8)} />
							<Typography appearance="body-75-02">{resources.disclaimerTwo}</Typography>
						</Box>
					)}
				</Box>
			</Box>
			<Spacer vertical={rem(32)} />
			<ComposableTextLink
				typography={{ text: resources.paymentTax, appearance: 'body-75-02' }}
				link={{
					text: resources.paymentTaxPrice,
					url: resources.url,
					appearance: 'body-75-03',
					colorToken: 'interaction-01-100',
				}}
			/>
			<Box
				styles={{
					position: 'absolute',
					zIndex: 140,
				}}
			>
				<Walkthrough currentStepIndex={0} steps={walkthroughSteps} />
			</Box>
		</ComponentInfo>
	);
};
